import React, { useEffect, useState } from "react";
import {
  getScheduleGroups,
  getScheduleGroupStatus,
} from "../utilities/genesysCloudUtils";
import { convertToStandardTime } from "../utilities/helperUtils";
import "../App.css";
import { DxButton } from "genesys-react-components";
import { Loading } from "./Loading"; // Import the Loading component
import { CatalogTableSurface } from "./CatalogTableSurface";

export function ScheduleGroups({
  checkedDivisions,
  setPageCount,
  pageNumber,
  setPageNumber,
  setTabName,
  opts,
  setOpts,
  setIsVisible,
  selectedExportRows = {},
  exportStatus,
  onClearExportSelection,
  onExportCsv,
  onToggleExportRow,
  onToggleVisibleExportRows,
}) {
  const [scheduleGroups, setScheduleGroups] = useState([]);
  const [loading, setLoading] = useState(true); // State to track loading status

  useEffect(() => {
    generateScheduleGroups();
  }, [pageNumber, checkedDivisions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageNumber(1);
  }, [checkedDivisions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTabName("schedule-groups-tab");
    setOpts({})
    setIsVisible({ divisions: true, object: false, applyButton: false })
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generateScheduleGroups = () => {
    setLoading(true); // Set loading to true when starting to fetch data
    let opts = {
      pageSize: 100, // Number | Page size
      pageNumber: pageNumber,
      sortBy: "name", // String | Sort by
      sortOrder: "ASC", // String | Ascending or descending sort order
      divisionId: checkedDivisions, // [String] | List of divisionIds on which to filter.
    };
    getScheduleGroups(opts).then((data) => {
      setScheduleGroups(data.entities);
      setPageCount(data.pageCount);

      const statusPromises = data.entities.map((scheduleGroup) =>
        getScheduleGroupStatus(scheduleGroup).then((status) => {
          return { id: scheduleGroup.id, status };
        })
      );

      Promise.all(statusPromises).then((statuses) => {
        setScheduleGroups((prev) =>
          prev.map((group) => {
            const status = statuses.find((s) => s.id === group.id);
            return { ...group, status: status ? status.status : null };
          })
        );
        setLoading(false); // Set loading to false when data fetching is complete
      });
    });
  };

  const allVisibleSelected = scheduleGroups.length > 0 && scheduleGroups.every((scheduleGroup) => selectedExportRows[scheduleGroup.id]);

  return (
    <CatalogTableSurface
      allVisibleSelected={allVisibleSelected}
      exportStatus={exportStatus}
      filteredExportAvailable={true}
      loading={loading}
      onClearSelection={onClearExportSelection}
      onExportCurrent={() => onExportCsv({ mode: "current", records: scheduleGroups })}
      onExportFiltered={() => onExportCsv({ mode: "all" })}
      onExportSelected={() => onExportCsv({ mode: "selected" })}
      onSelectVisible={(checked) => onToggleVisibleExportRows(scheduleGroups, checked)}
      rowCount={scheduleGroups.length}
      selectedCount={Object.keys(selectedExportRows).length}
      title="Schedule Groups"
    >
      {loading ? (
        <Loading />
      ) : (
        <>
      {scheduleGroups.length > 0 ? (
        <table className="table schedule-groups-table">
          <thead>
            <tr>
              <th aria-label="Select rows" className="select-column"></th>
              <th>Count</th>
              <th>Schedule Group Name</th>
              <th>Active</th>
              <th>Current</th>
              <th>Timezone</th>
              <th>Division</th>
              <th>Schedule Group Id</th>
            </tr>
          </thead>
          <tbody>
            {scheduleGroups.map((scheduleGroup, index) => {
              return (
                <tr key={scheduleGroup.id}>
                  <td className="select-column">
                    <input
                      aria-label={`Select ${scheduleGroup.name} for export`}
                      checked={Boolean(selectedExportRows[scheduleGroup.id])}
                      onChange={(event) => onToggleExportRow(scheduleGroup, event.target.checked)}
                      type="checkbox"
                    />
                  </td>
                  <th className="index">{index + 1}</th>
                  <td>
                    <div className="collapse collapse-arrow">
                      <input type="checkbox" />
                      <div className="collapse-title">{scheduleGroup.name}</div>
                      <div className="collapse-content">
                        <div className="overflow-x-auto">
                          <table className="table table-sm schedule-group-collection-table">
                            <thead>
                              <tr>
                                <th>Open</th>
                                <th>Closed</th>
                                <th>Holiday</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>
                                  {scheduleGroup.openSchedules &&
                                    scheduleGroup.openSchedules.map((open) => (
                                      <div key={open.id}>
                                        <div>{open.name}</div>
                                        <div></div>
                                      </div>
                                    ))}
                                </td>
                                <td>
                                  {scheduleGroup.closedSchedules &&
                                    scheduleGroup.closedSchedules.map(
                                      (closed) => (
                                        <div key={closed.id}>
                                          <div>{closed.name}</div>
                                          <div></div>
                                        </div>
                                      )
                                    )}
                                </td>
                                <td>
                                  {scheduleGroup.holidaySchedules &&
                                    scheduleGroup.holidaySchedules.map(
                                      (holiday) => (
                                        <div key={holiday.id}>
                                          <div>{holiday.name}</div>
                                          <div></div>
                                        </div>
                                      )
                                    )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {scheduleGroup.status && scheduleGroup.status.active ? (
                      <>
                        <DxButton
                          type="primary"
                          className={`schedule-active ${scheduleGroup.status.type === "Open"
                              ? "schedule-open"
                              : "schedule-closed"
                            }`}
                        >
                          {scheduleGroup.status.type}
                        </DxButton>

                        <div>
                          {scheduleGroup.status.active.start.slice(11, 16) ===
                            scheduleGroup.status.active.end.slice(11, 16)
                            ? "All Day"
                            : `${convertToStandardTime(
                              scheduleGroup.status.active.start.slice(11, 16)
                            )} -
                              ${convertToStandardTime(
                              scheduleGroup.status.active.end.slice(11, 16)
                            )}`}
                        </div>
                        <div>{scheduleGroup.status.active.name}</div>
                      </>
                    ) : (
                      <DxButton type="primary" className="schedule-inactive">
                        Inactive
                      </DxButton>
                    )}
                  </td>
                  <td>
                    {new Date().toLocaleString("en-US", {
                      timeZone: scheduleGroup.timeZone,
                    })}
                  </td>
                  <td>{scheduleGroup.timeZone}</td>
                  <td>{scheduleGroup.division.name}</td>
                  <td className="schedule-group-id">
                    <kbd className="kbd">{scheduleGroup.id}</kbd>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <h1>No Schedule Groups Found</h1>
      )}
        </>
      )}
    </CatalogTableSurface>
  );
}